import Udesk from 'Udesk';
import UdeskLocales from 'UdeskLocales';
import React, { useState, useMemo, useCallback, FC } from 'react';
import { useRequest } from 'ahooks';
import { usePageParams } from '../../hooks';
import { getLabel, useOptions } from 'src/pages/conversationAnalysis/hooks';
import { Button, Space, Popconfirm, message } from 'udesk-ui';
import ImageViewer from 'src/components/common/ImageViewer';
import styled from 'styled-components';
import Page from 'Component/common/udesk-qa-page-layout';
import UnrelatedProducts from './components/UnrelatedProducts';
import CommonTablePage from 'Component/common/udesk-qa-table-page';
import type { TableComponentProps } from 'Component/common/udesk-qa-table-page';
import { IndeterminateCircleOutlined } from '@udesk/icons';
import { getProductDetails } from 'src/api/productDetails';
import { putProductDetailsChangeMainProduct } from 'src/api/productDetails/changeMainProduct';
import { putProductDetailsRemoveMainProduct } from 'src/api/productDetails/removeMainProduct';
import { GetProductDetailsQuery, ProductDetailFoundResponse } from 'src/api/types';
import { getCurrentRouteName } from 'Udesk/system/subApp';

const Header = styled.div`
    margin-bottom: 20px;
    text-align: center;
`;

interface TemplateProps {
    history?: any;
}

const Template: FC<TemplateProps> = React.memo((props: any) => {
    const { history } = props;

    const { id, isReady } = usePageParams(props);

    const onBack = () => {
        Udesk.ui.routing.transitionTo({
            history,
            routeName: getCurrentRouteName('productManage'),
        });
    };

    const [products, setProducts] = useState<number[]>();

    const onProductsChange = (values: number[]) => {
        setProducts(values);
    };

    const onProductsSave = (open: boolean) => {
        if (!open && isReady && products?.length) {
            putProductDetailsChangeMainProduct({
                mainProductId: id,
                ids: products,
            }).then(() => {
                message.success(
                    /* 关联成功 */ UdeskLocales['current'].pages.conversationAnalysis.productManage
                        .components.subProduct.index.associationSuccess
                );
                setProducts([]);
                getTableData();
            });
        }
    };

    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [total, setTotal] = useState(0);
    const [dataSource, setDataSource] = useState<ProductDetailFoundResponse[]>([]);

    const { loading: getLoading, run: getTableData } = useRequest(
        (params?: GetProductDetailsQuery) =>
            getProductDetails({
                params: {
                    ...params,
                    mainProductId: id,
                    pageNum: params?.pageNum ?? 1,
                    pageSize: params?.pageSize ?? 20,
                },
            }),
        {
            ready: isReady,
            onBefore: () => {
                setTotal(0);
                setDataSource([]);
            },
            onSuccess: (res) => {
                setPageNum(res.paging?.pageNum ?? 1);
                setPageSize(res.paging?.pageSize ?? 20);
                setTotal(res.paging?.total ?? 0);
                setDataSource(res.data || []);
            },
        }
    );

    const { ECommerceTypeOptions } = useOptions();

    const onDel = useCallback(
        (id: number) => {
            if (id) {
                putProductDetailsRemoveMainProduct({ params: { id } }).then(() => {
                    message.success(
                        /* 操作成功 */ UdeskLocales['current'].pages.conversationAnalysis
                            .productManage.components.subProduct.index.operationSuccessful
                    );
                    getTableData();
                });
            }
        },
        [getTableData]
    );

    const columns = useMemo<any[]>(() => {
        return [
            {
                key: 'image',
                dataIndex: 'image',
                title: /* 产品图片 */ UdeskLocales['current'].pages.conversationAnalysis
                    .productManage.components.subProduct.index.productPicture,
                width: '100px',
                render(v, item) {
                    return v ? (
                        <ImageViewer>
                            <img width={80} src={v} alt="" />
                        </ImageViewer>
                    ) : (
                        '-'
                    );
                },
            },
            {
                key: 'code',
                dataIndex: 'code',
                title: /* 产品ID */ UdeskLocales['current'].pages.conversationAnalysis.productManage
                    .components.subProduct.index.productID,
                width: '10%',
            },
            {
                key: 'title',
                dataIndex: 'title',
                title: /* 产品标题 */ UdeskLocales['current'].pages.conversationAnalysis
                    .productManage.components.subProduct.index.productTitle,
                width: '15%',
            },
            {
                key: 'channelSource',
                dataIndex: 'channelSource',
                title: /* 电商平台 */ UdeskLocales['current'].pages.conversationAnalysis
                    .productManage.components.subProduct.index.ecommercePlatform,
                width: '10%',
                render: (v) => {
                    return getLabel(ECommerceTypeOptions, v);
                },
            },
            {
                key: 'price',
                dataIndex: 'price',
                title: /* 价格 */ UdeskLocales['current'].pages.conversationAnalysis.productManage
                    .components.subProduct.index.price,
                width: '10%',
                render: (v, item) => {
                    return v + item.priceUnit;
                },
            },
            {
                key: 'sales',
                dataIndex: 'sales',
                title: /* 销量 */ UdeskLocales['current'].pages.conversationAnalysis.productManage
                    .components.subProduct.index.sales,
                width: '10%',
            },
            {
                key: 'commentCount',
                dataIndex: 'commentCount',
                title: /* 评论数量 */ UdeskLocales['current'].pages.conversationAnalysis
                    .productManage.components.subProduct.index.numberOfComments,
                width: '10%',
            },
            {
                title: /* 操作 */ UdeskLocales['current'].pages.intelligentAnalysis.ruleTemplate
                    .index.operation,
                dataIndex: 'actions',
                key: 'actions',
                align: 'center',
                render: (name, item, index) => {
                    return (
                        <Space size="small">
                            <Popconfirm
                                title={
                                    /* 确定删除吗？ */ UdeskLocales['current'].pages
                                        .conversationAnalysis.productManage.components.subProduct
                                        .index.areYouSureToDeleteIt
                                }
                                onConfirm={() => onDel(item.id)}
                            >
                                <Button size="small" type="link">
                                    <IndeterminateCircleOutlined />
                                    {/* 移除 */}
                                    {
                                        UdeskLocales['current'].pages.conversationAnalysis
                                            .productManage.components.subProduct.index.remove
                                    }
                                </Button>
                            </Popconfirm>
                        </Space>
                    );
                },
            },
        ];
    }, [ECommerceTypeOptions, onDel]);

    const tableProps = useMemo<TableComponentProps>(() => {
        return {
            columns,
            dataSource,
            cacheKey: false,
            footerHeight: 16,
        };
    }, [columns, dataSource]);

    const paginationProps = useMemo(() => {
        return {
            current: pageNum,
            pageSize,
            total,
            onChange: (pageNum, pageSize) => {
                getTableData({
                    pageNum,
                    pageSize,
                });
            },
        };
    }, [getTableData, pageNum, pageSize, total]);

    return (
        <Page
            pageBodyClassName="xxx-page-index"
            title={
                /* 关联子产品 */ UdeskLocales['current'].pages.conversationAnalysis.productManage
                    .components.subProduct.index.associateSubproducts
            }
            padding={true}
            onBack={onBack}
        >
            <Header>
                <Space>
                    <div>
                        {/* 产品数据： */}
                        {
                            UdeskLocales['current'].pages.conversationAnalysis.productManage
                                .components.subProduct.index.productData
                        }
                    </div>
                    <UnrelatedProducts
                        value={products}
                        onChange={onProductsChange}
                        onDropdownVisibleChange={onProductsSave}
                    />
                </Space>
            </Header>

            <CommonTablePage loading={getLoading} hasHeader={false}>
                <CommonTablePage.Table {...tableProps}></CommonTablePage.Table>
                <CommonTablePage.Pagination {...paginationProps}></CommonTablePage.Pagination>
            </CommonTablePage>
        </Page>
    );
});

class Component extends React.Component {
    render() {
        return <Template {...this.props} />;
    }
}

export default Udesk.react.udeskify(Component);
